import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const parseStoredAmount = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value).replace(/,/g, "").replace(/[^0-9.-]/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeCurrencyText = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const resolveCurrencyLabel = (value, currencies = []) => {
  const normalizedValue = normalizeCurrencyText(value);
  if (!normalizedValue) return "-";

  for (const currency of currencies) {
    const candidates = [
      currency?.id,
      currency?.title,
      currency?.symbol,
      currency?.code,
      currency?.name,
    ]
      .filter(Boolean)
      .map(normalizeCurrencyText);

    if (candidates.includes(normalizedValue)) {
      return currency?.title || currency?.code || currency?.symbol || currency?.name || String(value);
    }
  }

  return String(value);
};

const getAgentItemsFromStorage = (agentId) => {
  if (typeof window === "undefined" || !agentId) return [];

  try {
    const raw = window.localStorage.getItem(`agent-items-${agentId}`);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const currencyMatches = (itemCurrency, selectedCurrency, currencies = []) => {
  if (!selectedCurrency) return true;

  const itemValue = normalizeCurrencyText(resolveCurrencyLabel(itemCurrency, currencies));
  const selectedValue = normalizeCurrencyText(resolveCurrencyLabel(selectedCurrency, currencies));
  return itemValue === selectedValue;
};

const buildCustomerSummaryRows = (items, currencies = []) => {
  const summaryMap = new Map();

  (Array.isArray(items) ? items : []).forEach((item) => {
    const currency = resolveCurrencyLabel(item?.currency, currencies);
    const key = String(item?.currency || currency || "-");

    if (!summaryMap.has(key)) {
      summaryMap.set(key, {
        key,
        currency: currency || key,
        totalAmount: 0,
        totalPaid: 0,
        totalRemaining: 0,
      });
    }

    const entry = summaryMap.get(key);
    const total = parseStoredAmount(item?.total);
    const paid = parseStoredAmount(item?.paid);
    entry.totalAmount += total;
    entry.totalPaid += paid;
    entry.totalRemaining += Math.max(total - paid, 0);
  });

  return Array.from(summaryMap.values());
};

const applyPaymentToStoredAgentItems = ({ agentId, amount, currencyId, currencyTitle, currencies, billRef }) => {
  if (typeof window === "undefined" || !agentId) return;

  const storageKey = `agent-items-${agentId}`;
  let storedItems = [];

  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = JSON.parse(raw);
    storedItems = Array.isArray(parsed) ? parsed : [];
  } catch {
    storedItems = [];
  }

  const paymentAmount = parseStoredAmount(amount);
  if (paymentAmount <= 0) return;

  let remaining = paymentAmount;
  const nextItems = storedItems.map((item) => {
    if (remaining <= 0) return item;

    if (billRef) {
      const itemBillRef = String(item?.billNo || item?.id || "");
      if (itemBillRef !== String(billRef)) {
        return item;
      }
    }

    if (
      !currencyMatches(item?.currency, currencyId, currencies) &&
      !currencyMatches(item?.currency, currencyTitle, currencies)
    ) {
      return item;
    }

    const total = parseStoredAmount(item?.total);
    const paid = parseStoredAmount(item?.paid);
    const available = Math.max(total - paid, 0);
    if (available <= 0) return item;

    const applied = Math.min(available, remaining);
    remaining -= applied;

    return {
      ...item,
      paid: paid + applied,
    };
  });

  window.localStorage.setItem(storageKey, JSON.stringify(nextItems));
  window.dispatchEvent(new Event("agent-items-updated"));
};

const CollectPaymentDialog = ({
  open,
  onClose,
  customer,
  token,
  onSuccess,
  summary,
  billRows = [],
  paymentRecords = [],
  onEditPayment,
  onDeletePayment,
}) => {
  const { t } = useTranslation();
  const [editingRecord, setEditingRecord] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editCurrencyId, setEditCurrencyId] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditAmount(String(record.amount || ""));
    setEditNotes(record.notes === "-" ? "" : record.notes || "");
    // Find currency id by title
    const matchedCurrency = filteredCurrencies.find(
      (c) => c.title === record.currency
    );
    setEditCurrencyId(matchedCurrency ? String(matchedCurrency.id) : "");
    onEditPayment?.(record);
  };

  const handleSaveEdit = async () => {
    if (!editingRecord) return;
    setEditLoading(true);
    try {
      const BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";
      const endpoint = editingRecord.recordType === "customer"
        ? `${BASE}/shop/customer-balance-payments/${editingRecord.id}/`
        : `${BASE}/shop/account-transactions/${editingRecord.id}/`;
      const payload = editingRecord.recordType === "customer"
        ? { amount: editAmount, currency: editCurrencyId || undefined, notes: editNotes }
        : { amount: editAmount, transaction_currency: editCurrencyId || undefined, description: editNotes };
      await axios.patch(endpoint, payload, {
        headers: { Authorization: `Token ${token}` },
      });
      setEditingRecord(null);
    } catch (err) {
      console.error("Failed to update payment:", err);
    }
    setEditLoading(false);
  };

  const {
    handleAddAsync: handleBalancePaymentAddAsync,
    loading: balancePaymentLoading,
  } = useAdd("shop/customer-balance-payments", token);
  const [transferPaymentLoading, setTransferPaymentLoading] = useState(false);

  const { data: currenciesData, isLoading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies",
    token
  );

  const currencies = currenciesData?.results ?? currenciesData ?? [];
  const filteredCurrencies = getUniqueCurrencies(currencies);

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "collect-payment"],
    "shop/accounts/?page_size=all",
    token
  );
  const accounts = Array.isArray(accountsData) ? accountsData : accountsData?.results || [];

  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("1");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 16)
  );
  const [notes, setNotes] = useState("");

  const storedAgentItems = useMemo(() => {
    if (Array.isArray(billRows) && billRows.length > 0) return billRows;
    if (Array.isArray(customer?.bill_items) && customer.bill_items.length > 0) return customer.bill_items;
    return getAgentItemsFromStorage(customer?.id);
  }, [billRows, customer?.bill_items, customer?.id, open]);
  const summaryRows = useMemo(() => {
    const sourceRows =
      Array.isArray(summary?.currencyBreakdown) && summary.currencyBreakdown.length
        ? summary.currencyBreakdown
        : buildCustomerSummaryRows(storedAgentItems, filteredCurrencies);

    return sourceRows
      .map((row) => ({
        key: String(row?.key || row?.currency || "-"),
        currency: String(row?.currency || "-"),
        totalAmount: parseStoredAmount(row?.totalAmount),
        totalPaid: parseStoredAmount(row?.totalPaid),
        totalRemaining: parseStoredAmount(row?.totalRemaining),
      }))
      .filter((row) => row.currency !== "-");
  }, [summary, storedAgentItems, filteredCurrencies]);
  const totalSummary = useMemo(
    () =>
      summaryRows.reduce(
        (acc, row) => ({
          totalAmount: acc.totalAmount + Number(row.totalAmount || 0),
          totalPaid: acc.totalPaid + Number(row.totalPaid || 0),
          totalRemaining: acc.totalRemaining + Number(row.totalRemaining || 0),
        }),
        { totalAmount: 0, totalPaid: 0, totalRemaining: 0 }
      ),
    [summaryRows]
  );
  const formatSummaryAmount = (value, currency = "") => {
    const amount = Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${amount} ${currency || "-"}`;
  };

  const resetForm = () => {
    setSelectedCurrency(null);
    setSelectedAccountId("");
    setAmount("");
    setExchangeRate("1");
    setPaymentDate(new Date().toISOString().slice(0, 16));
    setNotes("");
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  const isTransferGroup = customer?.recordType === "stock-transfer-group";
  const isSubmitting = balancePaymentLoading || transferPaymentLoading;

  const handleSubmit = async () => {
    if (!selectedCurrency || !selectedAccountId || !amount) {
      alert("Please fill all required fields.");
      return;
    }

    try {
      if (isTransferGroup) {
        setTransferPaymentLoading(true);
        const account = accounts.find((acc) => String(acc.id) === String(selectedAccountId));
        if (!account) throw new Error("Selected account not found.");
        
        const transferIds = customer?.items
          ?.map((item) => item?.sourceTransfer?.transfer_id)
          .filter(Boolean) || [];
        const metadata = `[Transfers: ${transferIds.join(", ")}]`;
        
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/shop/account-transactions/`,
          {
            account: Number(selectedAccountId),
            transaction_currency: Number(selectedCurrency.id),
            transaction_type: "credit",
            amount: Number(amount),
            exchange_rate: Number(exchangeRate) || 1,
            description: `${notes.trim()}${notes.trim() ? " " : ""}${metadata}`.trim(),
            transaction_date: paymentDate.slice(0, 10),
          },
          {
            headers: { Authorization: `Token ${token}` },
          }
        );
      } else {
        if (!customer?.id) throw new Error("Customer ID is missing.");
        
        await handleBalancePaymentAddAsync({
          customer: Number(customer.id),
          account: Number(selectedAccountId),
          currency: Number(selectedCurrency.id),
          amount: Number(amount),
          exchange_rate: Number(exchangeRate) || 1,
          apply_to_opening_balance: true,
          payment_date: paymentDate,
          notes: notes.trim() || undefined,
        });
      }

      applyPaymentToStoredAgentItems({
        agentId: customer?.id,
        amount,
        currencyId: selectedCurrency?.id,
        currencyTitle: selectedCurrency?.title,
        currencies: filteredCurrencies,
      });
      resetForm();
      onSuccess?.({
        amount: Number(amount),
        currencyId: selectedCurrency?.id,
      });
      onClose();
    } catch (error) {
      console.error("Payment error details:", error);
      const backendErrors = error.response?.data;
      let errorMsg = "Failed to record payment.";
      
      if (backendErrors && typeof backendErrors === "object") {
        const errorList = Object.entries(backendErrors).map(([key, value]) => {
          const field = key.charAt(0).toUpperCase() + key.slice(1);
          const message = Array.isArray(value) ? value.join(", ") : value;
          return `${field}: ${message}`;
        });
        errorMsg = errorList.join("\n") || errorMsg;
      } else {
        errorMsg = error.message || errorMsg;
      }
      
      alert('Payment Error:\n' + errorMsg);
    } finally {
      setTransferPaymentLoading(false);
    }
  };

  if (!customer) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { width: "50%", maxWidth: "50%" } }}
    >
      <DialogTitle>
        {t("customer.collectPayment", "Collect payment")} - {customer.name}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {t("customer.paymentDetails", "Payment Details")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {customer.name}
              </Typography>
            </Box>

            {summaryRows.length > 0 ? (
              <Box sx={{ display: "grid", gap: 1 }}>
                {summaryRows.map((row) => (
                  <Box
                    key={row.key}
                    sx={{
                      p: 1,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
                      {row.currency}
                    </Typography>
                    <Box sx={{ display: "grid", gap: 0.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                          {t("customer.totalAmount", "Total Amount")}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                          {formatSummaryAmount(row.totalAmount, row.currency)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                          {t("customer.totalPaid", "Total Paid")}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: "success.main", whiteSpace: "nowrap" }}>
                          {formatSummaryAmount(row.totalPaid, row.currency)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                          {t("customer.totalRemaining", "Total Remaining")}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: "error.main", whiteSpace: "nowrap" }}>
                          {formatSummaryAmount(row.totalRemaining, row.currency)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
                {summaryRows.length > 1 && (
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: "grey.100",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
                      {t("customer.overallTotal", "Overall Total")}
                    </Typography>
                    <Box sx={{ display: "grid", gap: 0.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                          {t("customer.totalAmount", "Total Amount")}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                          {formatSummaryAmount(totalSummary.totalAmount)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                          {t("customer.totalPaid", "Total Paid")}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: "success.main", whiteSpace: "nowrap" }}>
                          {formatSummaryAmount(totalSummary.totalPaid)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                          {t("customer.totalRemaining", "Total Remaining")}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: "error.main", whiteSpace: "nowrap" }}>
                          {formatSummaryAmount(totalSummary.totalRemaining)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("customer.noPaymentSummary", "No payment summary available.")}
              </Typography>
            )}
          </Box>

          {/* Row 1: Currency + Account */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <TextField
              select
              SelectProps={{ native: true }}
              margin="dense"
              label={t("salePayment.selectCurrency", "Currency")}
              fullWidth
              required
              value={selectedCurrency?.id ?? ""}
              onChange={(e) => {
                const c = filteredCurrencies.find((x) => String(x.id) === e.target.value);
                setSelectedCurrency(c || null);
              }}
              disabled={currencyLoading}
            >
              <option value="" />
              {(filteredCurrencies || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </TextField>
            <TextField
              select
              margin="dense"
              label={t("salePayment.account", "Account")}
              fullWidth
              required
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              disabled={accountLoading}
            >
              {accounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Row 2: Amount + Date */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <TextField
              margin="dense"
              label={t("salePayment.amount", "Amount")}
              type="number"
              fullWidth
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0, step: "0.01" }}
            />
            <TextField
              margin="dense"
              label={t("salePayment.paymentDate", "Payment date")}
              type="datetime-local"
              fullWidth
              required
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            margin="dense"
            label={t("salePayment.notes", "Notes")}
            multiline
            rows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {/* Payment History inside CollectPayment */}
          {paymentRecords.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                {t("agent.paymentHistory", "Payment History")}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "primary.main" }}>
                      <TableCell sx={{ color: "white" }}>{t("agent.reference", "Ref")}</TableCell>
                      <TableCell sx={{ color: "white" }}>{t("agent.amount", "Amount")}</TableCell>
                      <TableCell sx={{ color: "white" }}>{t("agent.currency", "Currency")}</TableCell>
                      <TableCell sx={{ color: "white" }}>{t("agent.paymentDate", "Date")}</TableCell>
                      <TableCell sx={{ color: "white" }}>{t("agent.notes", "Notes")}</TableCell>
                      <TableCell sx={{ color: "white" }}>{t("agent.action", "Action")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentRecords.map((record) => (
                      <TableRow key={`${record.recordType}-${record.reference}`} hover>
                        <TableCell>{record.reference}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "success.main" }}>
                          {editingRecord?.id === record.id && editingRecord?.recordType === record.recordType ? (
                            <TextField
                              type="number"
                              value={editAmount}
                              onChange={(e) => setEditAmount(e.target.value)}
                              size="small"
                              variant="standard"
                              sx={{ width: 100 }}
                            />
                          ) : (
                            Number(record.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          )}
                        </TableCell>
                        <TableCell>
                          {editingRecord?.id === record.id && editingRecord?.recordType === record.recordType ? (
                            <TextField
                              select
                              value={editCurrencyId}
                              onChange={(e) => setEditCurrencyId(e.target.value)}
                              size="small"
                              variant="standard"
                              sx={{ minWidth: 80 }}
                            >
                              {filteredCurrencies.map((c) => (
                                <MenuItem key={c.id} value={String(c.id)}>{c.title}</MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            record.currency
                          )}
                        </TableCell>                        <TableCell sx={{ fontSize: 12 }}>
                          {record.paymentDate ? new Date(record.paymentDate).toLocaleString() : "-"}
                        </TableCell>
                        <TableCell>
                          {editingRecord?.id === record.id && editingRecord?.recordType === record.recordType ? (
                            <TextField
                              value={editNotes}
                              onChange={(e) => setEditNotes(e.target.value)}
                              size="small"
                              variant="standard"
                              sx={{ width: 120 }}
                            />
                          ) : (
                            record.notes
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            {editingRecord?.id === record.id && editingRecord?.recordType === record.recordType ? (
                              <>
                                <Button size="small" variant="contained" color="success" disabled={editLoading} onClick={handleSaveEdit}
                                  sx={{ minWidth: 48, fontSize: 11, px: 1, py: 0.3 }}>
                                  {t("agent.save", "Save")}
                                </Button>
                                <Button size="small" color="secondary" disabled={editLoading} onClick={() => setEditingRecord(null)}
                                  sx={{ minWidth: 48, fontSize: 11, px: 1, py: 0.3 }}>
                                  {t("agent.cancel", "Cancel")}
                                </Button>
                              </>
                            ) : (
                              <>
                                <IconButton size="small" color="success" onClick={() => handleEditClick(record)}>
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => onDeletePayment?.(record)}>
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={isSubmitting}>
          {t("customer.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting || !selectedCurrency || !selectedAccountId || !amount}
          sx={{ minWidth: 100 }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("customer.collectPayment", "Collect Payment")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollectPaymentDialog;
